import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  // TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import useLabInternalStore from "../../store/useLabInternalStore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { formatDateTimeIST } from "../../utils/istDateTime";

const statusChipProps = (status) => {
  switch (status) {
    case "DRAFT":
      return { label: "DRAFT", color: "default" };
    case "ACTIVE":
      return { label: "ACTIVE", color: "primary" };
    case "CLOSED":
      return { label: "CLOSED", color: "success" };
    default:
      return { label: status, color: "default" };
  }
};

const FacultyLabInternalsPage = () => {
  const labs = useLabInternalStore((s) => s.facultyLabInternals);
  const safeLabs = Array.isArray(labs) ? labs : [];
  const loading = useLabInternalStore((s) => s.facultyLoading);
  const error = useLabInternalStore((s) => s.facultyError);
  const fetchFacultyLabInternals = useLabInternalStore((s) => s.fetchFacultyLabInternals);
  const deleteFacultyLabInternal = useLabInternalStore((s) => s.deleteFacultyLabInternal);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [labToDelete, setLabToDelete] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "error" });
  const navigate = useNavigate();
  const { isFaculty } = useAuth();

  React.useEffect(() => {
    fetchFacultyLabInternals();
  }, [fetchFacultyLabInternals, labToDelete]);

  const handleDeleteClick = (labId) => {
    setLabToDelete(labId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    if (labToDelete) {
      try {
        await deleteFacultyLabInternal(labToDelete);
        setSnackbar({ open: true, message: "Lab internal deleted successfully.", severity: "success" });
      } catch (err) {
        setSnackbar({ open: true, message: "Failed to delete lab internal. Please try again.", severity: "error" });
      }
      setLabToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setLabToDelete(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Lab Internals
      </Typography>
      {isFaculty && (
        <Button
          variant="contained"
          color="primary"
          sx={{ mb: 2 }}
          onClick={() => navigate("/faculty/lab-internals/new")}
        >
          Create Lab Internal
        </Button>
      )}
      <Paper elevation={1} sx={{ p: 2 }}>
        {loading && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Loading lab internals...
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {typeof error === 'string' ? error : error?.message || 'Something went wrong'}
          </Typography>
        )}
        {!loading && !error && safeLabs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No labs available.
          </Typography>
        )}
        {!loading && safeLabs.length > 0 && (
          <TableContainer>
          <Table size="medium" aria-label="lab internals table">
            <TableBody>
              {safeLabs.map((lab) => {
                const chipProps = statusChipProps(lab.status);
                return (
                  <TableRow key={lab._id} hover>
                    <TableCell>{lab.title}</TableCell>
                    <TableCell>{lab.courseId}</TableCell>
                    <TableCell>{formatDateTimeIST(lab.startDate, "-")}</TableCell>
                    <TableCell>{formatDateTimeIST(lab.endDate, "-")}</TableCell>
                    <TableCell>
                      <Chip size="small" {...chipProps} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            // console.log("Row", lab);
                            navigate(`/faculty/lab-internals/${lab._id}/edit`);
                          }}
                        >
                          View
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            navigate(`/faculty/lab-internals/${lab._id}/evaluate`);
                          }}
                        >
                          Evaluate
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => navigate(`/faculty/lab-results?labId=${encodeURIComponent(lab._id)}`)}
                        >
                          Results
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(lab.labId)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        sx={{ zIndex: 1300, p:12, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 2 }} // Ensure it appears above other content
      >
        <DialogTitle id="delete-dialog-title">Delete Lab Internal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this lab internal? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacultyLabInternalsPage;
